// @givmo/compliance — everyone the organization's signing requests hold sign-in details for, for a
// host's Settings page (CCM-R1-SIGN-REDACT-UNFINISHED-REQUESTS (web); backend `GET …/signing-people`):
// Org Machinery OM-17 seam B, a module-owned panel after the sign-in check's card.
//
// ══ WHOM IT REACHES ════════════════════════════════════════════════════════════════════════════
//
// A completed request's record card offers the removal for everyone its record holds sign-in details
// for (CCM-R1-SIGN-REDACT-EARLIER-SIGNERS (web)). A request that never completed — cancelled,
// expired, or replaced by a newer one — has no record, and once the workbench shows a newer request
// for its filing, no card at all; its acts still hold their signers' details, and it holds its
// sender's email. This list names everyone the organization's requests hold them for, newest first,
// whatever became of each request, and each row opens the record card's dialog with that person
// chosen, directed on the request the read names for them.
//
// ══ WHO SEES IT ════════════════════════════════════════════════════════════════════════════════
//
// A host declares it on `compliance:send_for_signature`, the string the removal and the read both
// require (D-SIGN-2: a Compliance Owner or a Global Admin). It is released only with signing, so
// where signing is dark nobody holds it and the card is absent; a read that answers 404 (signing
// dark) renders nothing too.
//
// ══ HOW IT NAMES THEM ═══════════════════════════════════════════════════════════════════════════
//
// As the dialog does, from the host's roster (`organizationMembers`): someone it lists by their
// Team-page name, someone it doesn't as no longer in the organization. Until the roster is read the
// card shows no rows (cut 1008-105's review, F2). A person who arrives on two pages, pushed across by
// someone new at the top between the two reads, is listed once (F5). The rows sit flush in the
// card, a rule between them, as the console's other Settings lists do (F12).
//
// Words: V1–V5, APPROVED (D-CCM-115, Jonathan 2026-10-22 07:16 PDT, "approve"): every line as
// cut 1008-105's review page drafts it. A row's name is the dialog's (W4, W4-a, W4-b); "Load more"
// and "Loading…" are the console's.

import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorMessage,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import { useCompliance } from './compliance-context';
import { isModuleUnavailable, serverMessage } from './compliance-decode';
import { signingPeopleQueryKey } from './compliance-queries';
import { decodeSigningPeople, type SigningPerson } from './compliance-signing-decode';
import { RemovePersonSignInDetailsDialog, namedPerson, rosterOf } from './signing-identity-removal';

const PREFIX = 'compliance-signing-people';
/** One page of the list: twenty people, then Load more. */
const PAGE_LIMIT = 20;

/** V1, the card's title. */
export const SIGNING_PEOPLE_TITLE = 'Sign-in details on signing requests';
/** V2, the card's lead. */
export const SIGNING_PEOPLE_LEAD =
  'Givmo keeps the sign-in email, IP address and browser of everyone who signed, declined or sent a request for signature, including requests that were never completed. You can remove a person’s details from all of them.';
/** V3, a person's second line: how many requests hold their details. */
export const onSigningRequests = (count: number) =>
  count === 1 ? 'On 1 signing request' : `On ${count} signing requests`;
/** V4, a row's act, which opens the removal dialog. */
export const REMOVE_DETAILS_FOR_ONE = 'Remove details…';
/** V5, nobody's details are kept. */
export const NO_SIGN_IN_DETAILS = 'There are no sign-in details to remove.';

/** Each person once, where they first appear. */
function oncePerPerson(people: readonly SigningPerson[]): SigningPerson[] {
  const seen = new Set<string>();
  return people.filter((person) => {
    if (seen.has(person.userId)) return false;
    seen.add(person.userId);
    return true;
  });
}

export function ComplianceSigningPeopleCard() {
  const { orgRef, isReady, operations, moduleStanding, organizationMembers } = useCompliance();
  const [removing, setRemoving] = useState<SigningPerson | null>(null);
  const available = isReady && moduleStanding === 'available' && orgRef !== null;
  const list = useInfiniteQuery({
    queryKey: signingPeopleQueryKey(orgRef),
    queryFn: async ({ pageParam }) =>
      decodeSigningPeople(
        await operations.listSigningPeople(orgRef ?? '', { limit: PAGE_LIMIT, offset: pageParam }),
      ),
    initialPageParam: 0,
    // A full page may have more after it; a short one is the last.
    getNextPageParam: (last, pages) =>
      last.length < PAGE_LIMIT ? undefined : pages.reduce((n, page) => n + page.length, 0),
    enabled: available,
  });

  if (!available) return null;
  if (list.error != null && isModuleUnavailable(list.error)) return null;
  const people = oncePerPerson(list.data?.pages.flat() ?? []);
  const roster = rosterOf(organizationMembers);

  return (
    <Card {...testIdProp(PREFIX)}>
      <CardHeader>
        <div className="min-w-0">
          <h3 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
            {SIGNING_PEOPLE_TITLE}
          </h3>
          <p className="mt-2 max-w-[640px] text-s13 leading-h15 text-balance text-text-tertiary">
            {SIGNING_PEOPLE_LEAD}
          </p>
        </div>
      </CardHeader>
      {list.isPending || roster === undefined ? (
        <CardBody className="px-20 py-16">
          <Skeleton className="h-[120px] w-full" testId={`${PREFIX}-loading`} />
        </CardBody>
      ) : roster === null || (list.isError && people.length === 0) ? (
        <CardBody className="px-20 py-16">
          <ErrorMessage testId={`${PREFIX}-read-error`}>
            {roster === null ? 'Please try again.' : serverMessage(list.error, 'Please try again.')}
          </ErrorMessage>
        </CardBody>
      ) : people.length === 0 ? (
        <CardBody className="px-20 py-16">
          <p className="text-s13 leading-h15 text-text-tertiary" {...testIdProp(`${PREFIX}-empty`)}>
            {NO_SIGN_IN_DETAILS}
          </p>
        </CardBody>
      ) : (
        <ul className="divide-y divide-border-subtle" {...testIdProp(`${PREFIX}-list`)}>
          {people.map((person) => {
            const id = `${PREFIX}-row-${person.userId}`;
            return (
              <li
                key={person.userId}
                className="flex items-center justify-between gap-16 px-20 py-14"
                {...testIdProp(id)}
              >
                <div className="min-w-0">
                  <p className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                    {namedPerson(person, roster).label}
                  </p>
                  <p className="mt-2 font-sans text-s13 tracking-givmo text-text-tertiary">
                    {onSigningRequests(person.requests)}
                  </p>
                </div>
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  className="shrink-0"
                  onClick={() => setRemoving(person)}
                  testId={`${id}-remove`}
                >
                  {REMOVE_DETAILS_FOR_ONE}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      {list.hasNextPage && roster !== undefined && roster !== null && (
        <CardBody className="border-t border-border-subtle px-20 py-16">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            disabled={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
            testId={`${PREFIX}-load-more`}
          >
            {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </CardBody>
      )}
      {removing !== null && (
        <RemovePersonSignInDetailsDialog person={removing} open onClose={() => setRemoving(null)} />
      )}
    </Card>
  );
}
